const MONEY_ERROR = "El valor monetario no es válido.";

function canonicalizeParts(integerPart, decimalPart = "", negative = false) {
  const integer = integerPart.replace(/^0+(?=\d)/, "") || "0";
  const decimals = decimalPart.padEnd(2, "0");
  const sign = negative && (integer !== "0" || decimals !== "00") ? "-" : "";
  return `${sign}${integer}.${decimals}`;
}

function splitSign(raw, allowNegative) {
  if (!raw.startsWith("-")) return { negative: false, unsigned: raw };
  if (!allowNegative) throw new TypeError("El valor monetario no puede ser negativo.");
  return { negative: true, unsigned: raw.slice(1) };
}

/** Parse a value typed by a person using Colombian separators.
 * Point is exclusively a thousands separator and comma is the decimal mark.
 */
export function parseColombianMoneyInput(value, { allowNegative = false } = {}) {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new TypeError(MONEY_ERROR);
    if (value < 0 && !allowNegative) {
      throw new TypeError("El valor monetario no puede ser negativo.");
    }
    return normalizeApiMoney(String(value), { allowNegative });
  }

  const raw = String(value ?? "").trim();
  if (!raw) throw new TypeError("El valor monetario está vacío.");
  if (/\s|[eE]|[+]/.test(raw)) throw new TypeError(MONEY_ERROR);

  const { negative, unsigned } = splitSign(raw, allowNegative);
  const pieces = unsigned.split(",");
  if (pieces.length > 2) throw new TypeError(MONEY_ERROR);

  const [integerPart, decimalPart = ""] = pieces;
  if (!integerPart || !/^\d+(?:\.\d{3})*$/.test(integerPart)) {
    throw new TypeError(MONEY_ERROR);
  }
  if (integerPart.includes(".") && !/^\d{1,3}(?:\.\d{3})+$/.test(integerPart)) {
    throw new TypeError(MONEY_ERROR);
  }
  if (pieces.length === 2 && !/^\d{1,2}$/.test(decimalPart)) {
    throw new TypeError(MONEY_ERROR);
  }

  return canonicalizeParts(integerPart.replace(/\./g, ""), decimalPart, negative);
}

/** Normalize a decimal received from (or already intended for) the API.
 * A point is exclusively the decimal mark; thousands separators are invalid.
 */
export function normalizeApiMoney(value, { allowNegative = true } = {}) {
  if (typeof value === "number" && !Number.isFinite(value)) {
    throw new TypeError(MONEY_ERROR);
  }
  const raw = String(value ?? "").trim();
  if (!raw) throw new TypeError("El valor monetario está vacío.");
  if (/\s|[eE,+]/.test(raw)) throw new TypeError(MONEY_ERROR);

  const { negative, unsigned } = splitSign(raw, allowNegative);
  const match = unsigned.match(/^(\d+)(?:\.(\d{1,2}))?$/);
  if (!match) throw new TypeError(MONEY_ERROR);
  return canonicalizeParts(match[1], match[2] || "", negative);
}

export function formatApiMoneyForInput(value, options) {
  const canonical = normalizeApiMoney(value, options);
  const [integer, decimals] = canonical.split(".");
  return `${integer},${decimals}`;
}

// Compatibility for non-editable legacy callers. New input flows must choose
// parseColombianMoneyInput or normalizeApiMoney explicitly.
export function parseMoney(value) {
  return Number(normalizeApiMoney(value));
}

export function moneyEquals(left, right) {
  try {
    return normalizeApiMoney(left) === normalizeApiMoney(right);
  } catch {
    return false;
  }
}
